/**
 * TEMPORARY – Theme test toggle.
 * Uses the real useRestaurantTheme hook for the default theme.
 * The hook injects a <style> element; this toggle overrides with inline styles.
 * When "Restaurant Theme" is selected, inline overrides are removed → hook's styles show.
 * Delete this file once validation is done.
 */
import { useState, useEffect, useMemo } from 'react';
import { useTenant } from '@core/tenant/TenantProvider';
import {
  hexToHsl,
  autoContrastText,
  normalizeCustomStyle,
  buildThemeCssVars,
  hasAlpha,
  THEME_CSS_VAR_KEYS,
  type ThemeConfig,
} from '@core/tenant/useRestaurantTheme';

// ── Admin preset bases (from meitre-admin design.ts) ─────────────────

const LIGHT_BASE = {
  bodyColor: '#e5e5ea',
  headerColor: '#ffffff',
  textSubHeaderColor: '#666666',
};

const DARK_BASE = {
  bodyColor: '#1d1d1d',
  headerColor: '#333333',
  textSubHeaderColor: '#cccccc',
};

const MAIN_COLORS: Record<string, string> = {
  Black:     '#000000',
  Red:       '#ff3b30',
  Grey:      '#c7c7cc',
  Blue:      '#007aff',
  'Dark Blue': '#173fcb',
  Yellow:    '#ffcc00',
  Green:     '#4cd964',
  White:     '#ffffff',
};

// ── Theme entry shape ────────────────────────────────────────────────

interface ThemeEntry {
  label: string;
  group: string;
  /** null = remove inline overrides (show restaurant theme or CSS defaults) */
  config: ThemeConfig | null;
  /** Optional bodyImage URL to overlay over the restaurant's real body image */
  bodyImage?: string;
}

// ── Build admin presets ──────────────────────────────────────────────

function buildAdminPresets(): ThemeEntry[] {
  const entries: ThemeEntry[] = [];
  for (const [colorName, mainColor] of Object.entries(MAIN_COLORS)) {
    entries.push({
      label: `Light + ${colorName}`,
      group: 'Admin: Light Base',
      config: { mainColor, mainColorText: autoContrastText(mainColor), ...LIGHT_BASE },
    });
  }
  for (const [colorName, mainColor] of Object.entries(MAIN_COLORS)) {
    entries.push({
      label: `Dark + ${colorName}`,
      group: 'Admin: Dark Base',
      config: { mainColor, mainColorText: autoContrastText(mainColor), ...DARK_BASE },
    });
  }
  return entries;
}

// ── Static test themes ──────────────────────────────────────────────

const CUSTOM_THEMES: ThemeEntry[] = [
  {
    label: 'Very Light Body + Dark Brand',
    group: 'Custom: Edge Cases',
    config: {
      mainColor: '#1a1a1a', mainColorText: '#ffffff',
      bodyColor: '#fafafa', headerColor: '#ffffff', textSubHeaderColor: '#444444',
    },
  },
  {
    label: 'Pure White Everything',
    group: 'Custom: Edge Cases',
    config: {
      mainColor: '#ffffff', mainColorText: '#000000',
      bodyColor: '#ffffff', headerColor: '#ffffff', textSubHeaderColor: '#333333',
    },
  },
  {
    label: 'Pure Black Everything',
    group: 'Custom: Edge Cases',
    config: {
      mainColor: '#000000', mainColorText: '#ffffff',
      bodyColor: '#000000', headerColor: '#111111', textSubHeaderColor: '#e0e0e0',
    },
  },
  {
    label: 'High Saturation Pink',
    group: 'Custom: Edge Cases',
    config: {
      mainColor: '#ff1493', mainColorText: '#ffffff',
      bodyColor: '#fff0f5', headerColor: '#ffffff', textSubHeaderColor: '#4a4a4a',
    },
  },
  {
    label: 'Low Contrast (Hard to Read)',
    group: 'Custom: Edge Cases',
    config: {
      mainColor: '#cccccc', mainColorText: '#ffffff',
      bodyColor: '#e8e8e8', headerColor: '#f0f0f0', textSubHeaderColor: '#999999',
    },
  },
  // ── Production restaurant configs (real API data) ──────────────────
  {
    label: 'Test (Dark + White brand)',
    group: 'Production: Real Restaurants',
    config: {
      mainColor: '#ffffff', mainColorText: autoContrastText('#ffffff'),
      bodyColor: '#1d1d1d', headerColor: '#333333', textSubHeaderColor: '#cccccc',
    },
  },
  {
    label: 'Ritual (Dark + White brand)',
    group: 'Production: Real Restaurants',
    config: {
      mainColor: '#ffffff', mainColorText: autoContrastText('#ffffff'),
      bodyColor: '#1d1d1d', headerColor: '#333333', textSubHeaderColor: '#cccccc',
    },
  },
  {
    label: 'Don Julio (Dark gray + Gold)',
    group: 'Production: Real Restaurants',
    config: {
      mainColor: '#a67e4b', mainColorText: autoContrastText('#a67e4b'),
      bodyColor: '#404040', headerColor: '#ffffff', textSubHeaderColor: '#000000',
    },
  },
  {
    label: 'Casa Palanti (Mauve + Burgundy)',
    group: 'Production: Real Restaurants',
    config: {
      mainColor: '#450c0b', mainColorText: autoContrastText('#450c0b'),
      bodyColor: '#9d8787', headerColor: '#ffffff', textSubHeaderColor: '#450c0b',
    },
  },
  {
    label: 'Evelia (Light + Black brand)',
    group: 'Production: Real Restaurants',
    config: {
      mainColor: '#000000', mainColorText: autoContrastText('#000000'),
      bodyColor: '#b0b0b3', headerColor: '#f7ecdc', textSubHeaderColor: '#000000',
    },
  },
  // ── Production restaurants with transparent customStyle (real API data) ─
  // These exercise the alpha-channel handling: bg-card etc. must honor the
  // restaurant's intended translucency over the body image.
  {
    label: 'Cosme (header 80% white)',
    group: 'Production: With Transparency',
    config: {
      mainColor: '#2d2d2d', mainColorText: autoContrastText('#2d2d2d'),
      bodyColor: '#ffffffff', headerColor: '#ffffffcc', textSubHeaderColor: '#000000',
    },
  },
  {
    label: 'Abrasado (header 82% gray)',
    group: 'Production: With Transparency',
    config: {
      mainColor: '#000000', mainColorText: autoContrastText('#000000'),
      bodyColor: '#ffffffff', headerColor: '#b1b4b4d1', textSubHeaderColor: '#000000',
    },
  },
  {
    label: 'Mishiguene (90% black both)',
    group: 'Production: With Transparency',
    config: {
      mainColor: '#efb360', mainColorText: autoContrastText('#efb360'),
      bodyColor: '#000000e7', headerColor: '#000000e7', textSubHeaderColor: '#efb360',
    },
  },
  {
    label: 'Nicky Harrison (80% blue header)',
    group: 'Production: With Transparency',
    config: {
      mainColor: '#000000', mainColorText: autoContrastText('#000000'),
      bodyColor: '#e5e5ea', headerColor: '#3c6395cc', textSubHeaderColor: '#f3f3f3',
    },
  },
  {
    label: 'Jet Lag (92% black header)',
    group: 'Production: With Transparency',
    config: {
      mainColor: '#ffffff', mainColorText: autoContrastText('#ffffff'),
      bodyColor: '#1d1d1d', headerColor: '#000000eb', textSubHeaderColor: '#ffffff',
    },
  },
  {
    label: '1900 (92% white over blue body)',
    group: 'Production: With Transparency',
    config: {
      mainColor: '#0e398b', mainColorText: autoContrastText('#0e398b'),
      bodyColor: '#0e398b', headerColor: '#ffffffea', textSubHeaderColor: '#c5162d',
    },
  },
  {
    label: 'Namm (67% white header)',
    group: 'Production: With Transparency',
    config: {
      mainColor: '#000000', mainColorText: autoContrastText('#000000'),
      bodyColor: '#ffffff', headerColor: '#ffffffaa', textSubHeaderColor: '#000000',
    },
  },
  {
    label: 'Checkpoint Charlie (60% white header)',
    group: 'Production: With Transparency',
    config: {
      mainColor: '#173442', mainColorText: autoContrastText('#173442'),
      bodyColor: '#ffffff', headerColor: '#ffffff99', textSubHeaderColor: '#173442',
    },
  },
  // ── Sampled production restaurants with real bodyImage (2026-05) ────
  // Pulled from the May 2026 transparency sample (n=40, 12 translucent).
  // bodyImage overrides the document.body background so the alpha channel
  // bleeds against the same image the real restaurant ships.
  {
    label: 'Villegas Resto (65% white header)',
    group: 'Sampled: Real bodyImage',
    config: {
      mainColor: '#000000', mainColorText: autoContrastText('#000000'),
      bodyColor: '#2f2020', headerColor: '#ffffffa6', textSubHeaderColor: '#e27b2e',
    },
    bodyImage: 'https://assets-meitre-frontend.s3.amazonaws.com/villegasresto/body_1723560285.jpeg',
  },
  {
    label: 'Bodega Trattoria (80% white header)',
    group: 'Sampled: Real bodyImage',
    config: {
      mainColor: '#cf0828', mainColorText: autoContrastText('#cf0828'),
      bodyColor: '#ffffff', headerColor: '#ffffffcc', textSubHeaderColor: '#cf0828',
    },
    bodyImage: 'https://assets-meitre-frontend.s3.amazonaws.com/bodegadelatrattoria-megaplaza/body_1741003911.jpeg',
  },
  {
    label: 'Estación La Serena (80% white header)',
    group: 'Sampled: Real bodyImage',
    config: {
      mainColor: '#000000', mainColorText: autoContrastText('#000000'),
      bodyColor: '#ffffff', headerColor: '#ffffffcc', textSubHeaderColor: '#000000',
    },
    bodyImage: 'https://assets-meitre-frontend.s3.amazonaws.com/estacionlaserena/body_1776865519.jpeg',
  },
  {
    label: 'Evvai (94% white body + header)',
    group: 'Sampled: Real bodyImage',
    config: {
      mainColor: '#082263', mainColorText: autoContrastText('#082263'),
      bodyColor: '#ffffffef', headerColor: '#ffffffef', textSubHeaderColor: '#666666',
    },
    bodyImage: 'https://assets-meitre-frontend.s3.amazonaws.com/evvai/body_1612386004.jpeg',
  },
  {
    label: 'Fan London (68% black header)',
    group: 'Sampled: Real bodyImage',
    config: {
      mainColor: '#ffffff', mainColorText: autoContrastText('#ffffff'),
      bodyColor: '#ffffff', headerColor: '#000000ad', textSubHeaderColor: '#ffffff',
    },
    bodyImage: 'https://assets-meitre-frontend.s3.amazonaws.com/fan-london/body_1748897479.jpeg',
  },
  {
    label: 'Lugar Infinito (80% black header)',
    group: 'Sampled: Real bodyImage',
    config: {
      mainColor: '#ffffff', mainColorText: autoContrastText('#ffffff'),
      bodyColor: '#ffffff', headerColor: '#000000cc', textSubHeaderColor: '#ffffff',
    },
    bodyImage: 'https://assets-meitre-frontend.s3.amazonaws.com/lugarinfinitocafe/body_1749490112.jpeg',
  },
  {
    label: 'El Chacal Cumbayá (80% white header)',
    group: 'Sampled: Real bodyImage',
    config: {
      mainColor: '#000000', mainColorText: autoContrastText('#000000'),
      bodyColor: '#ffffff', headerColor: '#ffffffcc', textSubHeaderColor: '#000000',
    },
    bodyImage: 'https://assets-meitre-frontend.s3.amazonaws.com/elchacalparrillacumbaya/body_1734133052.jpeg',
  },
  {
    label: 'Oliver Miraflores (82% teal header)',
    group: 'Sampled: Real bodyImage',
    config: {
      mainColor: '#ffffff', mainColorText: autoContrastText('#ffffff'),
      bodyColor: '#ffffff', headerColor: '#1c373dd2', textSubHeaderColor: '#ffffff',
    },
    bodyImage: 'https://assets-meitre-frontend.s3.amazonaws.com/oliver-miraflores/body_1741004368.jpeg',
  },
  {
    label: 'Trade Sky Bar (85% white header)',
    group: 'Sampled: Real bodyImage',
    config: {
      mainColor: '#000000', mainColorText: autoContrastText('#000000'),
      bodyColor: '#ffffff', headerColor: '#ffffffda', textSubHeaderColor: '#000000',
    },
    bodyImage: 'https://assets-meitre-frontend.s3.amazonaws.com/trade-sky-bar/body_1651701696.jpeg',
  },
  {
    label: 'Musgo (61% white header + brand alpha)',
    group: 'Sampled: Real bodyImage',
    config: {
      mainColor: '#9b816d', mainColorText: autoContrastText('#9b816d'),
      bodyColor: '#ffffff', headerColor: '#ffffff9b', textSubHeaderColor: '#9b816d',
    },
    bodyImage: 'https://assets-meitre-frontend.s3.amazonaws.com/musgo/body_1714659382.jpeg',
  },
  {
    label: 'CipóRestô (80% white header)',
    group: 'Sampled: Real bodyImage',
    config: {
      mainColor: '#48432c', mainColorText: autoContrastText('#48432c'),
      bodyColor: '#ffffff', headerColor: '#ffffffcc', textSubHeaderColor: '#48432c',
    },
    bodyImage: 'https://assets-meitre-frontend.s3.amazonaws.com/ciporesto/body_1635803921.jpeg',
  },
  {
    label: 'Café Misterio (87% white header)',
    group: 'Sampled: Real bodyImage',
    config: {
      mainColor: '#bd2100', mainColorText: autoContrastText('#bd2100'),
      bodyColor: '#ffffff', headerColor: '#ffffffdd', textSubHeaderColor: '#666666',
    },
    bodyImage: 'https://assets-meitre-frontend.s3.amazonaws.com/cafemisterio/body_1665512228.jpeg',
  },
  {
    label: 'Piso 21 (80% white header)',
    group: 'Sampled: Real bodyImage',
    config: {
      mainColor: '#000000', mainColorText: autoContrastText('#000000'),
      bodyColor: '#ffffff', headerColor: '#ffffffcc', textSubHeaderColor: '#000000',
    },
    bodyImage: 'https://assets-meitre-frontend.s3.amazonaws.com/piso21/body_1633188159.jpeg',
  },
];

// ── System Gold defaults (matches index.css :root) ──────────────────

const SYSTEM_GOLD: ThemeEntry = {
  label: 'System Gold (index.css defaults)',
  group: 'System',
  config: {
    mainColor: '#B29061',
    mainColorText: '#FFFFFF',
    bodyColor: '#FFFFFF',
    headerColor: '#FFFFFF',
    textSubHeaderColor: '#1a1a1a',
  },
};

// ── Inline style helpers ────────────────────────────────────────────

function applyInlineVars(vars: Record<string, string>) {
  const root = document.documentElement;
  for (const [key, value] of Object.entries(vars)) {
    root.style.setProperty(key, value);
  }
}

function clearInlineVars() {
  const root = document.documentElement;
  for (const key of THEME_CSS_VAR_KEYS) {
    root.style.removeProperty(key);
  }
}

function applyBodyImage(url: string) {
  const { style } = document.body;
  style.backgroundImage = `url("${url}")`;
  style.backgroundSize = 'cover';
  style.backgroundPosition = 'center';
  style.backgroundAttachment = 'fixed';
  style.backgroundRepeat = 'no-repeat';
}

// ── Component ───────────────────────────────────────────────────────

export function ThemeTestToggle() {
  const { restaurant, subdomainPrefix } = useTenant();
  const [currentIdx, setCurrentIdx] = useState(0);
  const [isOpen, setIsOpen] = useState(false);

  // Build the full theme list with the real restaurant theme as index 0
  const allThemes = useMemo(() => {
    const restaurantConfig = restaurant?.customStyle
      ? normalizeCustomStyle(restaurant.customStyle)
      : null;

    const restaurantEntry: ThemeEntry = {
      label: restaurantConfig
        ? `Restaurant Theme (${restaurant?.name || 'current'})`
        : 'Restaurant Theme (no customStyle)',
      group: 'Active',
      config: null, // null = remove inline overrides → hook's <style> shows through
    };

    return [
      restaurantEntry,
      SYSTEM_GOLD,
      ...buildAdminPresets(),
      ...CUSTOM_THEMES,
    ];
  }, [restaurant]);

  const groups = useMemo(() => {
    const groupMap = new Map<string, { index: number; label: string }[]>();
    allThemes.forEach((entry, index) => {
      const list = groupMap.get(entry.group) || [];
      list.push({ index, label: entry.label });
      groupMap.set(entry.group, list);
    });
    return Array.from(groupMap.entries()).map(([group, entries]) => ({ group, entries }));
  }, [allThemes]);

  const isTestSubdomain = subdomainPrefix === 'test';

  const restaurantBodyImage = restaurant?.customStyle?.bodyImage;

  useEffect(() => {
    if (!isTestSubdomain) return;
    const entry = allThemes[currentIdx];
    if (!entry) return;

    if (entry.config === null) {
      // "Restaurant Theme" — remove inline overrides so hook's <style> takes effect
      clearInlineVars();
    } else {
      // Any test theme — apply inline overrides (higher specificity than hook's <style>)
      const vars = buildThemeCssVars(entry.config);
      applyInlineVars(vars);
      // Mirror useRestaurantTheme: the translucent: variant keys off this class,
      // so presets must toggle it to render as they do in production.
      const translucent = hasAlpha(entry.config.bodyColor) || hasAlpha(entry.config.headerColor);
      document.documentElement.classList.toggle('theme-translucent', translucent);
    }

    if (entry.bodyImage) {
      applyBodyImage(entry.bodyImage);
    } else if (restaurantBodyImage) {
      applyBodyImage(restaurantBodyImage);
    }

    return () => {
      clearInlineVars();
      document.documentElement.classList.remove('theme-translucent');
      if (restaurantBodyImage) applyBodyImage(restaurantBodyImage);
    };
  }, [currentIdx, allThemes, isTestSubdomain, restaurantBodyImage]);

  const currentEntry = allThemes[currentIdx];

  // For display: resolve what's actually showing
  const displayConfig = currentEntry?.config
    ?? (restaurant?.customStyle ? normalizeCustomStyle(restaurant.customStyle) : null);
  const isDark = displayConfig ? hexToHsl(displayConfig.headerColor).l < 40 : false;
  const swatchColor = displayConfig?.mainColor || '#C9A96E';

  // Only render on "test" subdomain
  if (!isTestSubdomain) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        zIndex: 9999,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 8,
        fontFamily: 'Inter, system-ui, sans-serif',
      }}
    >
      {isOpen && (
        <div
          style={{
            background: isDark ? '#2a2a2a' : '#ffffff',
            color: isDark ? '#e0e0e0' : '#1a1a1a',
            borderRadius: 12,
            boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
            padding: 16,
            width: 320,
            maxHeight: 'calc(100vh - 100px)',
            overflowY: 'auto',
            border: `1px solid ${isDark ? '#444' : '#e0e0e0'}`,
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 12, opacity: 0.6 }}>
            Theme Tester ({allThemes.length} themes)
          </div>

          <select
            value={currentIdx}
            onChange={(e) => setCurrentIdx(Number(e.target.value))}
            style={{
              width: '100%',
              padding: '8px 12px',
              borderRadius: 8,
              border: `1px solid ${isDark ? '#555' : '#ccc'}`,
              background: isDark ? '#1a1a1a' : '#f5f5f5',
              color: isDark ? '#e0e0e0' : '#1a1a1a',
              fontSize: 13,
              cursor: 'pointer',
              marginBottom: 12,
            }}
          >
            {groups.map(({ group, entries }) => (
              <optgroup key={group} label={group}>
                {entries.map(({ index, label }) => (
                  <option key={index} value={index}>
                    {label}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>

          {displayConfig && (
            <div style={{ fontSize: 11, lineHeight: 1.6, opacity: 0.8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <span>mainColor:</span>
                <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: 3, background: displayConfig.mainColor, border: '1px solid rgba(128,128,128,0.3)' }} />
                <code style={{ fontSize: 11 }}>{displayConfig.mainColor}</code>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <span>bodyColor:</span>
                <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: 3, background: displayConfig.bodyColor, border: '1px solid rgba(128,128,128,0.3)' }} />
                <code style={{ fontSize: 11 }}>{displayConfig.bodyColor}</code>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <span>headerColor:</span>
                <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: 3, background: displayConfig.headerColor, border: '1px solid rgba(128,128,128,0.3)' }} />
                <code style={{ fontSize: 11 }}>{displayConfig.headerColor}</code>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <span>textColor:</span>
                <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: 3, background: displayConfig.textSubHeaderColor, border: '1px solid rgba(128,128,128,0.3)' }} />
                <code style={{ fontSize: 11 }}>{displayConfig.textSubHeaderColor}</code>
              </div>
              <div style={{ fontSize: 10, marginTop: 6, opacity: 0.5 }}>
                Mode: {hexToHsl(displayConfig.headerColor).l < 40 ? 'Dark' : 'Light'} card
                {' / '}
                {hexToHsl(displayConfig.bodyColor).l < 40 ? 'Dark' : 'Light'} body
                {currentEntry?.config === null && ' (from API)'}
              </div>
            </div>
          )}

          {!displayConfig && (
            <div style={{ fontSize: 11, opacity: 0.5 }}>
              No customStyle from API — using CSS defaults
            </div>
          )}
        </div>
      )}

      <button
        onClick={() => setIsOpen(!isOpen)}
        style={{
          padding: '8px 16px',
          borderRadius: 9999,
          background: isDark ? '#fff' : '#000',
          color: isDark ? '#000' : '#fff',
          fontSize: 12,
          fontWeight: 600,
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          cursor: 'pointer',
          border: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <span style={{ display: 'inline-block', width: 12, height: 12, borderRadius: '50%', background: swatchColor, border: '1px solid rgba(128,128,128,0.4)' }} />
        {isOpen ? 'CLOSE' : 'THEMES'}
      </button>
    </div>
  );
}
